// React import removed as JSX transform handles it;

// Material-UI
import { Box, CssBaseline } from '@mui/material';
import { Navigate, Route, BrowserRouter as Router, Routes, useLocation } from 'react-router-dom';
// Components
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';
// Auth & Contexts
import { AuthProvider } from './contexts/AuthContext';
import { GroupProvider } from './contexts/GroupContext';
import { NotificationProvider } from './contexts/NotificationContext';
// Theme
import { ThemeProvider } from './contexts/ThemeContext';
import { WorkspaceProvider } from './contexts/WorkspaceContext';
import ComplianceCheck from './pages/ComplianceCheck';
import CreateItem from './pages/CreateItem';
import CreateVideoItem from './pages/CreateVideoItem';
import DocumentProcessing from './pages/DocumentProcessing';
import DomainFactCheck from './pages/DomainFactCheck';
import FactCheckWorkflow from './pages/FactCheckWorkflow';
import Help from './pages/Help';
import InvestigationPage from './pages/InvestigationPage';
import InvitePage from './pages/InvitePage';
import ItemDetail from './pages/ItemDetail';
import Login from './pages/Login';
// Pages
import MyItems from './pages/MyItems';
import MyPage from './pages/MyPage';
import ProcessingResults from './pages/ProcessingResults';
import SampleWorkspace from './pages/SampleWorkspace';
import Settings from './pages/Settings';
import VideoProcessing from './pages/VideoProcessing';
import WorkspaceList from './pages/WorkspaceList';

/**
 * 案件の道を /workspaces から /share に変えたが、通知に古いリンクが残っているので
 * 続きの部分（案件のIDなど）をそのまま付け替えて転送する。
 */
function RedirectWorkspaces() {
  const { pathname, search, hash } = useLocation();
  const rest = pathname.replace(/^\/workspaces/, '');
  return <Navigate to={`/share${rest}${search}${hash}`} replace />;
}

function App() {
  return (
    <ThemeProvider>
      <CssBaseline />
      <Router>
        <AuthProvider>
          <NotificationProvider>
            <GroupProvider>
              <WorkspaceProvider>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 'var(--app-vh)',
                    width: 'var(--app-vw)',
                    margin: 0,
                    padding: 0,
                    boxSizing: 'border-box',
                  }}
                >
                  <Routes>
                    {/* 認証ページ */}
                    <Route path="/login" element={<Login />} />

                    {/* トップ（できることの窓口だけ） */}
                    <Route
                      path="/"
                      element={
                        <ProtectedRoute>
                          <Layout>
                            <MyPage />
                          </Layout>
                        </ProtectedRoute>
                      }
                    />

                    {/* マイアイテム（検証した資料の一覧 ＋ AIによる追加リサーチ）。
                        以前はトップに同居していたが、トップを窓口だけにしたので分けた（#150） */}
                    <Route
                      path="/items"
                      element={
                        <ProtectedRoute>
                          <Layout>
                            <MyItems />
                          </Layout>
                        </ProtectedRoute>
                      }
                    />

                    {/* 規程チェック（横断・専用画面） */}
                    <Route
                      path="/compliance"
                      element={
                        <ProtectedRoute>
                          <Layout>
                            <ComplianceCheck />
                          </Layout>
                        </ProtectedRoute>
                      }
                    />

                    {/* 以前の /workspaces 宛て。通知に古いリンクが残っているので転送する */}
                    <Route path="/workspaces" element={<Navigate to="/share" replace />} />
                    <Route path="/workspaces/*" element={<RedirectWorkspaces />} />

                    {/* 案件一覧 */}
                    <Route
                      path="/share"
                      element={
                        <ProtectedRoute>
                          <Layout>
                            <WorkspaceList />
                          </Layout>
                        </ProtectedRoute>
                      }
                    />

                    {/* 見本の案件（まだグループが無い人向け）。:id より先に置く */}
                    <Route
                      path="/share/sample"
                      element={
                        <ProtectedRoute>
                          <Layout>
                            <SampleWorkspace />
                          </Layout>
                        </ProtectedRoute>
                      }
                    />

                    {/* 案件の中身。グループ共有の画面の真ん中に出すので、同じ画面を使う。
                        直リンクでも開けるよう、URL は従来どおり残している。 */}
                    <Route
                      path="/share/:id"
                      element={
                        <ProtectedRoute>
                          <Layout>
                            <WorkspaceList />
                          </Layout>
                        </ProtectedRoute>
                      }
                    />

                    {/* アイテム詳細（factcheck_items - 下書き等） */}
                    <Route
                      path="/items/:itemId"
                      element={
                        <ProtectedRoute>
                          <Layout>
                            <ItemDetail />
                          </Layout>
                        </ProtectedRoute>
                      }
                    />

                    {/* 結果詳細（completed/error状態のアイテム） */}
                    <Route
                      path="/results/:itemId"
                      element={
                        <ProtectedRoute>
                          <Layout>
                            <ItemDetail />
                          </Layout>
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/create"
                      element={
                        <ProtectedRoute>
                          <Layout>
                            <CreateItem />
                          </Layout>
                        </ProtectedRoute>
                      }
                    />

                    {/* 動画アップロード */}
                    <Route
                      path="/create-video"
                      element={
                        <ProtectedRoute>
                          <Layout>
                            <CreateVideoItem />
                          </Layout>
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/video/processing/:itemId"
                      element={
                        <ProtectedRoute>
                          <Layout>
                            <VideoProcessing />
                          </Layout>
                        </ProtectedRoute>
                      }
                    />

                    {/* スキャンPDFの非同期OCR処理中画面 */}
                    <Route
                      path="/document/processing/:sessionId"
                      element={
                        <ProtectedRoute>
                          <Layout>
                            <DocumentProcessing />
                          </Layout>
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/workflow/:sessionId"
                      element={
                        <ProtectedRoute>
                          <Layout>
                            <FactCheckWorkflow />
                          </Layout>
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/processing/:sessionId"
                      element={
                        <ProtectedRoute>
                          <Layout>
                            <ProcessingResults />
                          </Layout>
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/investigation"
                      element={
                        <ProtectedRoute>
                          <Layout>
                            <InvestigationPage />
                          </Layout>
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/investigation/:sessionId"
                      element={
                        <ProtectedRoute>
                          <Layout>
                            <InvestigationPage />
                          </Layout>
                        </ProtectedRoute>
                      }
                    />

                    {/* ドメイン限定ファクトチェック */}
                    <Route
                      path="/domain-factcheck"
                      element={
                        <ProtectedRoute>
                          <Layout>
                            <DomainFactCheck />
                          </Layout>
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/help"
                      element={
                        <ProtectedRoute>
                          <Layout>
                            <Help />
                          </Layout>
                        </ProtectedRoute>
                      }
                    />

                    {/* 設定ページ */}
                    <Route
                      path="/settings"
                      element={
                        <ProtectedRoute>
                          <Layout>
                            <Settings />
                          </Layout>
                        </ProtectedRoute>
                      }
                    />

                    {/* 招待リンクページ */}
                    <Route path="/invite/:groupId/:inviteId" element={<InvitePage />} />
                  </Routes>
                </Box>
              </WorkspaceProvider>
            </GroupProvider>
          </NotificationProvider>
        </AuthProvider>
      </Router>
    </ThemeProvider>
  );
}

export default App;
